import React, {
 useState
} from 'react';
import {
 Tabs, TabsList, TabsTrigger, TabsContent
} from 'shared/components/ui/tabs';
import {
 Badge
} from 'shared/components/ui/badge';
import {
 Database, Cable
} from 'lucide-react';
import {
 SchemaListItem
} from '../../types/schema.types';
import SchemaPropertiesTab from './SchemaPropertiesTab';
import SchemaDataTab from './SchemaDataTab';

type DetailTab = 'properties' | 'data';

interface SchemaDetailPanelProps {
    schema: SchemaListItem | null;
    connectionName: string | null;
    onSchemaSaved: () => void;
}

const SchemaDetailPanel: React.FC<SchemaDetailPanelProps> = ({
    schema, connectionName, onSchemaSaved,
}) => {
    const [activeTab, setActiveTab] = useState<DetailTab>('properties');

    if (!schema) {
        return (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-center p-8">
                <div className="rounded-full bg-muted p-6">
                    <Database className="h-10 w-10 text-muted-foreground" />
                </div>
                <div>
                    <h3 className="text-lg font-semibold">Select a Schema</h3>
                    <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                        Choose a schema from the explorer tree on the left to view its properties and data.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b">
                <div className="flex items-center gap-3 min-w-0">
                    <Database className="h-5 w-5 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                        <h2 className="text-lg font-semibold truncate">{schema.tableName}</h2>
                        {connectionName && (
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                <Cable className="h-3 w-3" />
                                <span>{connectionName}</span>
                            </div>
                        )}
                    </div>
                    <Badge variant="secondary" className="text-xs shrink-0">
                        {schema.fieldCount} fields
                    </Badge>
                </div>
            </div>

            {/* Tabs */}
            <Tabs
                value={activeTab}
                onValueChange={(v) => setActiveTab(v as DetailTab)}
                className="flex flex-col flex-1 min-h-0"
            >
                <div className="border-b px-4">
                    <TabsList className="h-10 bg-transparent p-0">
                        <TabsTrigger
                            value="properties"
                            className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none"
                        >
                            Properties
                        </TabsTrigger>
                        <TabsTrigger
                            value="data"
                            className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none"
                        >
                            Data
                        </TabsTrigger>
                    </TabsList>
                </div>

                <TabsContent value="properties" className="flex-1 overflow-auto p-4 mt-0">
                    <SchemaPropertiesTab
                        key={schema.tableName}
                        schema={schema}
                        onSaved={onSchemaSaved}
                    />
                </TabsContent>

                <TabsContent value="data" className="flex-1 overflow-auto p-4 mt-0">
                    <SchemaDataTab
                        key={schema.tableName}
                        schema={schema}
                    />
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default SchemaDetailPanel;
